import { AxleDialogShell } from '@/dialogs/AxleDialogShell/AxleDialogShell';
import { AxleErrorBoundary } from '@/fetching/errorBoundaries';
import { PAGE_BODY_SKELETON_HEIGHT, SimpleSkeleton } from '@/fetching/suspense';
import { Children, ReturnComponent } from '@/utils/reactTypes';
import { Breakpoint, Button, ButtonProps } from '@mui/material';
import { Suspense } from 'react';

import { AxleDialogActions, AxleDialogContent } from './AxleDialog';

export interface IAxleConfirmDialogProps {
    children: Children;
    swapButtonOrder?: boolean;
    cancelCta?: string;
    cancelButtonProps?: Omit<ButtonProps, 'onClick'>;
    close: () => void;
    confirmCta: string;
    confirmButtonEnabled?: boolean;
    confirmButtonProps?: Omit<ButtonProps, 'onClick'>;
    onConfirm: () => void;
    width?: Breakpoint;
    title?: string;
}

type AxleConfirmDialogContentProps = Omit<IAxleConfirmDialogProps, 'width' | 'title'> & {
    cancelCta: string;
    confirmButtonEnabled: boolean;
};

type AxleConfirmDialogButtonsProps = Omit<AxleConfirmDialogContentProps, 'children'>;

// The cancel and confirm pair, in the order the caller asked for.
export const AxleConfirmDialogButtons = ({
    cancelCta,
    cancelButtonProps = {},
    swapButtonOrder = false,
    close,
    confirmCta,
    confirmButtonEnabled,
    confirmButtonProps = {},
    onConfirm,
}: AxleConfirmDialogButtonsProps): ReturnComponent => {
    const cancelButton = (
        <Button
            key={'cancel'}
            fullWidth
            variant='outlined'
            color='primary'
            {...cancelButtonProps}
            onClick={close}
        >
            {cancelCta}
        </Button>
    );
    const confirmButton = (
        <Button
            key={'confirm'}
            fullWidth
            variant='outlined'
            color='error'
            disabled={!confirmButtonEnabled}
            {...confirmButtonProps}
            onClick={onConfirm}
        >
            {confirmCta}
        </Button>
    );

    return <>{swapButtonOrder ? [confirmButton, cancelButton] : [cancelButton, confirmButton]}</>;
};

export const AxleConfirmDialogContent = ({
    children,
    ...buttonProps
}: AxleConfirmDialogContentProps) => (
    <>
        <AxleDialogContent>{children}</AxleDialogContent>
        <AxleDialogActions>
            <AxleConfirmDialogButtons {...buttonProps} />
        </AxleDialogActions>
    </>
);

export const AxleConfirmDialog = ({
    isOpen,
    close,
    children,
    title = 'Confirm',
    confirmCta,
    cancelCta = 'Cancel',
    swapButtonOrder,
    cancelButtonProps,
    onConfirm,
    confirmButtonEnabled = true,
    confirmButtonProps,
    width = 'sm',
}: IAxleConfirmDialogProps & {
    isOpen: boolean;
    close: () => void;
}): ReturnComponent => {
    /* Rendered by whoever asks the question rather than through the dialog manager, and so
     * without the manager's flag-resolved shell behavior. A confirmation belongs to the thing it
     * confirms: several of these open from inside another dialog, where a managed one would
     * render at the app root and outlive the dialog that asked. It is not draggable for the same
     * reason, so it cannot be pulled off what it belongs to.
     *
     * AxleDialog wrapped its children in an error boundary and a suspense boundary and
     * AxleDialogShell does not, so this keeps both around the question's body.
     */
    return (
        <AxleDialogShell
            dataTestId={'confirm-dialog'}
            title={title}
            maxWidth={width}
            open={isOpen}
            onClose={close}
            footer={
                <AxleConfirmDialogButtons
                    swapButtonOrder={swapButtonOrder}
                    cancelCta={cancelCta}
                    cancelButtonProps={cancelButtonProps}
                    close={close}
                    confirmCta={confirmCta}
                    confirmButtonEnabled={confirmButtonEnabled}
                    confirmButtonProps={confirmButtonProps}
                    onConfirm={onConfirm}
                />
            }
        >
            <AxleErrorBoundary>
                <Suspense fallback={<SimpleSkeleton height={PAGE_BODY_SKELETON_HEIGHT} />}>
                    {children}
                </Suspense>
            </AxleErrorBoundary>
        </AxleDialogShell>
    );
};
